import React, { useState, useEffect } from "react";
import { Modal, Form, Input, InputNumber, Button, Typography, Divider, notification, Checkbox } from "@pankod/refine-antd";
import axios from "axios";
import { API_URL, TOKEN_KEY } from "../../constants/environment";

const { Title, Text } = Typography;

interface RentPlacePaymentModalProps {
  visible: boolean;
  onCancel: () => void;
  onOk: () => void;
  recordId: string | number;
  eventName?: string;
  isLoading?: boolean;
  existingPayment?: {
    rental_cost?: string | number;
    rental_discount?: string | number;
    rental_total?: string | number;
    payment_comment?: string;
    needs_invoice?: boolean;
  };
}

export const RentPlacePaymentModal: React.FC<RentPlacePaymentModalProps> = ({
  visible,
  onCancel,
  onOk,
  recordId,
  eventName,
  isLoading = false,
  existingPayment
}) => {
  const [form] = Form.useForm();
  const [cost, setCost] = useState<number>(0);
  const [discount, setDiscount] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [processing, setProcessing] = useState(false);

  // Calcular total automáticamente
  useEffect(() => {
    const calculatedTotal = Math.max(0, cost - discount);
    setTotal(calculatedTotal);
    form.setFieldsValue({ rental_total: calculatedTotal });
  }, [cost, discount, form]);

  // Resetear formulario cuando se cierra el modal
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setCost(0);
      setDiscount(0);
      setTotal(0);
    }
  }, [visible, form]);

  // Inicializar formulario con datos existentes si están disponibles
  useEffect(() => {
    if (visible && existingPayment) {
      const initialCost = Number(existingPayment.rental_cost) || 0;
      const initialDiscount = Number(existingPayment.rental_discount) || 0;
      
      setCost(initialCost);
      setDiscount(initialDiscount);
      
      form.setFieldsValue({
        rental_cost: initialCost,
        rental_discount: initialDiscount,
        payment_comment: existingPayment.payment_comment || "",
        needs_invoice: existingPayment.needs_invoice || false,
      });
    }
  }, [visible, existingPayment, form]);

  const handlePayment = async (values: any) => {
    setProcessing(true);
    const token = localStorage.getItem(TOKEN_KEY);

    try {
      const payload = {
        rental_cost: values.rental_cost || 0,
        rental_discount: values.rental_discount || 0,
        payment_comment: values.payment_comment || "",
        needs_invoice: values.needs_invoice || false,
      };

      await axios.patch(`${API_URL}/rent-places/${recordId}/pay`, payload, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      notification.success({
        message: "Pago registrado exitosamente",
        description: "El evento ha sido marcado como pagado.",
      });

      form.resetFields();
      onOk();
    } catch (error: any) {
      notification.error({
        message: "Error al procesar el pago",
        description: error?.response?.data?.message || "Ocurrió un error al procesar el pago.",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal 
      title="Procesar Pago de Renta"
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong>Evento: </Text>
        <Text>{eventName || "Sin nombre"}</Text>
      </div>
      
      <Divider />

      <Form
        form={form}
        layout="vertical"
        onFinish={handlePayment}
        initialValues={{ rental_cost: 0, rental_discount: 0, rental_total: 0 }}
      >
        <Form.Item
          name="rental_cost"
          label="Costo de la Renta"
          rules={[
            { required: true, message: "El costo es requerido" },
            { type: "number", min: 0, message: "El costo debe ser mayor o igual a 0" }
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="0.00"
            prefix="$"
            precision={2}
            onChange={(value) => setCost(Number(value) || 0)}
          />
        </Form.Item>

        <Form.Item
          name="rental_discount"
          label="Descuento"
          rules={[
            { type: "number", min: 0, message: "El descuento debe ser mayor o igual a 0" }
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="0.00"
            prefix="$"
            precision={2}
            onChange={(value) => setDiscount(Number(value) || 0)}
          />
        </Form.Item>

        <Form.Item
          name="rental_total"
          label="Total a Pagar"
        >
          <InputNumber
            style={{ 
              width: "100%", 
              backgroundColor: "#f5f5f5",
              fontSize: "18px",
              fontWeight: "bold"
            }}
            prefix="$"
            precision={2}
            value={total}
            disabled
          />
        </Form.Item>

        <Form.Item
          name="payment_comment"
          label="Comentario de Pago (Opcional)"
        >
          <Input.TextArea
            rows={3}
            placeholder="Agregar una nota sobre el pago..."
            maxLength={500}
          />
        </Form.Item>

        <Form.Item
          name="needs_invoice"
          valuePropName="checked"
        >
          <Checkbox>
            ¿Necesita factura?
          </Checkbox>
        </Form.Item>

        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Button onClick={handleCancel} style={{ marginRight: 8 }}>
            Cancelar
          </Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={processing || isLoading}
            disabled={total <= 0}
          >
            Procesar Pago
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
